// Advanced Image Optimization for Shopify
// This script provides aggressive image optimization focused on:
// 1. Early loading of LCP images with proper sizing
// 2. Preventing layout shifts through explicit dimensions
// 3. WebP image conversion with quality optimization
// 4. Responsive image loading with efficient sizes

document.addEventListener('DOMContentLoaded', function() {
  // Immediate pre-execution to prevent layout shifts
  const setDefaultDimensions = () => {
    document.querySelectorAll('img:not([width]):not([height])').forEach(img => {
      // Set placeholder dimensions to prevent CLS
      if (img.hasAttribute('src') && img.src) {
        img.style.aspectRatio = 'auto';
        if (img.classList.contains('banner__media-image') || 
            img.classList.contains('slideshow__image') || 
            img.parentElement.classList.contains('banner__media')) {
          img.style.aspectRatio = '16/9';
        } else if (img.classList.contains('product__media-image')) {
          img.style.aspectRatio = '1/1';
        }
      }
    });
  };
  setDefaultDimensions();

  // Connect to Performance Observer to monitor LCP
  if ('PerformanceObserver' in window) {
    try {
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lcpElement = entries[entries.length - 1];
        if (lcpElement && lcpElement.element && lcpElement.element.tagName === 'IMG') {
          // We found our LCP element - prioritize it
          console.log('LCP element identified:', lcpElement.element);
          const img = lcpElement.element;
          
          // Force high priority loading
          img.setAttribute('fetchpriority', 'high');
          img.setAttribute('loading', 'eager');
          
          // Remove any delaying attributes
          if (img.style.opacity !== '1') {
            img.style.opacity = '1';
          }
          
          // Stop observing once we've found the LCP
          lcpObserver.disconnect();
        }
      });
      
      lcpObserver.observe({type: 'largest-contentful-paint', buffered: true});
    } catch (e) {
      console.log('PerformanceObserver for LCP not supported');
    }
  }

  // Identify potential LCP candidates (hero images, banner images)
  const potentialLCPImages = document.querySelectorAll('.slideshow__image, .banner__media img, .header__heading-logo, .hero-banner img, .featured-collection img:first-child');
  
  // Mark LCP candidates with high priority and disable lazy loading
  potentialLCPImages.forEach(img => {
    if (img) {
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', 'high');
      img.classList.add('critical-image');
      
      // Ensure dimensions are set for LCP images to prevent layout shifts
      if (!img.hasAttribute('width') || !img.hasAttribute('height')) {
        img.style.aspectRatio = 'auto 16/9';
      }
    }
  });

  // Find all non-critical images
  const allImages = document.querySelectorAll('img:not(.critical-image)');
  
  // Check for WebP support with fallback
  const supportsWebP = () => {
    const canvas = document.createElement('canvas');
    if (canvas.getContext && canvas.getContext('2d')) {
      return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    }
    return false;
  };
  
  const hasWebPSupport = supportsWebP();
  
  // Optimized function to create properly sized srcset for Shopify CDN
  const createOptimizedSrcset = (src) => {
    if (!src || !src.includes('cdn.shopify.com')) return null;
    
    // Extract base URL and parameters
    const baseUrl = src.split('?')[0];
    const params = new URLSearchParams(src.split('?')[1] || '');
    const width = params.get('width') || '';
    
    if (!width) return null;
    
    // Define optimal responsive widths to reduce variants
    const widths = [200, 400, 600, 900, 1200, 1600, 2000];
    const srcSetParts = [];
    
    // Generate optimized srcset attribute with quality reduction for larger sizes
    for (const w of widths) {
      if (w <= parseInt(width) * 1.5) { // Don't go too far beyond original size
        const newParams = new URLSearchParams(params);
        newParams.set('width', w.toString());
        
        // Add WebP format if supported with optimized quality
        if (hasWebPSupport) {
          newParams.set('format', 'webp');
          
          // Optimize quality based on image size
          if (w > 1200) {
            newParams.set('quality', '80'); // Lower quality for large images
          } else if (w > 600) {
            newParams.set('quality', '85'); // Medium quality for medium images
          }
        }
        
        srcSetParts.push(`${baseUrl}?${newParams.toString()} ${w}w`);
      }
    }
    
    return srcSetParts.join(', ');
  };

  // Optimize sizes attribute based on image's context/placement
  const getOptimizedSizes = (img) => {
    // Default sizes value
    let sizes = '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw';
    
    // Determine context-appropriate sizes
    if (img.closest('.banner') || img.closest('.slideshow') || img.closest('.hero-section')) {
      sizes = '100vw'; // Full width for hero images
    } else if (img.closest('.collection-list') || img.closest('.product-grid')) {
      sizes = '(max-width: 767px) 50vw, (max-width: 1024px) 33vw, 25vw';
    } else if (img.closest('.product__media') || img.closest('.product-image')) {
      sizes = '(max-width: 767px) 80vw, (max-width: 1024px) 50vw, 450px';
    }
    
    return sizes;
  };

  // Process all non-critical images with advanced optimizations
  allImages.forEach(img => {
    // Set explicit dimensions to prevent layout shifts if missing
    if (!img.hasAttribute('width') && !img.hasAttribute('height') && img.src) {
      const container = img.parentElement;
      if (container) {
        // Use container dimensions as base
        img.style.width = '100%';
        img.style.aspectRatio = 'auto';
      }
    }
    
    // Add lazy loading with appropriate margins
    if (!img.hasAttribute('loading')) {
      img.setAttribute('loading', 'lazy');
      
      // Use low priority for images far down the page
      if (img.getBoundingClientRect().top > window.innerHeight * 2) {
        img.setAttribute('fetchpriority', 'low');
      }
    }
    
    // Add srcset for responsive images with optimized paths
    const src = img.getAttribute('src');
    if (src) {
      const srcset = createOptimizedSrcset(src);
      if (srcset) {
        img.setAttribute('srcset', srcset);
        
        // Add optimized sizes if not present
        if (!img.hasAttribute('sizes')) {
          img.setAttribute('sizes', getOptimizedSizes(img));
        }
        
        // Convert src to WebP with quality optimization as fallback
        if (hasWebPSupport && src.includes('cdn.shopify.com')) {
          try {
            const srcUrl = new URL(src);
            srcUrl.searchParams.set('format', 'webp');
            
            // Optimize quality based on viewport
            if (window.innerWidth < 768) {
              srcUrl.searchParams.set('quality', '85');
            }
            
            img.setAttribute('src', srcUrl.toString());
          } catch (e) {
            // Fallback if URL parsing fails
            if (src.includes('?')) {
              img.setAttribute('src', `${src}&format=webp`);
            } else {
              img.setAttribute('src', `${src}?format=webp`);
            }
          }
        }
      }
    }
  });
  
  // Convert background images to WebP with quality optimization
  if (hasWebPSupport) {
    const elementsWithBgImage = document.querySelectorAll('[style*="background-image"]');
    elementsWithBgImage.forEach(el => {
      const style = el.getAttribute('style');
      if (style && style.includes('cdn.shopify.com') && !style.includes('format=webp')) {
        // Replace background image URLs with optimized WebP
        const newStyle = style.replace(/background-image:\s*url\(['"]?(https:\/\/cdn\.shopify\.com[^)'"]+)['"]?\)/g, (match, url) => {
          // Add WebP and quality parameters
          if (url.includes('?')) {
            return `background-image: url(${url}&format=webp&quality=85)`;
          } else {
            return `background-image: url(${url}?format=webp&quality=85)`;
          }
        });
        el.setAttribute('style', newStyle);
      }
    });
  }
  
  // Preload crucial images for LCP
  const preloadLCPImages = () => {
    // Take just the first highest priority LCP candidate
    const lcpImage = Array.from(potentialLCPImages)[0];
    
    if (lcpImage && lcpImage.getAttribute('src')) {
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      
      // Get the optimal source
      if (lcpImage.getAttribute('srcset')) {
        // Use appropriate size from srcset based on viewport
        const srcsetItems = lcpImage.getAttribute('srcset').split(',');
        const viewportWidth = window.innerWidth;
        
        // Find best match for current viewport
        let bestSource = lcpImage.getAttribute('src');
        let bestWidth = 0;
        
        srcsetItems.forEach(item => {
          const [url, width] = item.trim().split(' ');
          const numWidth = parseInt(width.replace('w', ''));
          
          // Select image that's just larger than viewport width
          if (numWidth >= viewportWidth && (bestWidth === 0 || numWidth < bestWidth)) {
            bestSource = url;
            bestWidth = numWidth;
          }
        });
        
        preloadLink.href = bestSource;
        
        // Add imagesrcset and imagesizes for browsers that support it
        preloadLink.setAttribute('imagesrcset', lcpImage.getAttribute('srcset'));
        if (lcpImage.getAttribute('sizes')) {
          preloadLink.setAttribute('imagesizes', lcpImage.getAttribute('sizes'));
        }
      } else {
        preloadLink.href = lcpImage.getAttribute('src');
      }
      
      // Add the preload to head
      document.head.appendChild(preloadLink);
    }
  };
  
  // Execute LCP image preloading
  preloadLCPImages();
  
  // Process deferred images that come in later (e.g., from AJAX)
  const processDynamicImages = () => {
    const newImages = document.querySelectorAll('img:not([processed-by-optimizer])');
    newImages.forEach(img => {
      // Skip already processed images
      if (img.hasAttribute('processed-by-optimizer')) return;
      
      // Set the flag to avoid reprocessing
      img.setAttribute('processed-by-optimizer', 'true');
      
      // Apply same optimizations as for initial images
      if (!img.classList.contains('critical-image')) {
        if (!img.hasAttribute('loading')) {
          img.setAttribute('loading', 'lazy');
        }
        
        const src = img.getAttribute('src');
        if (src) {
          const srcset = createOptimizedSrcset(src);
          if (srcset) {
            img.setAttribute('srcset', srcset);
            if (!img.hasAttribute('sizes')) {
              img.setAttribute('sizes', getOptimizedSizes(img));
            }
            
            // Convert to WebP if supported
            if (hasWebPSupport && src.includes('cdn.shopify.com')) {
              try {
                const srcUrl = new URL(src);
                srcUrl.searchParams.set('format', 'webp');
                img.setAttribute('src', srcUrl.toString());
              } catch (e) {
                // Fallback
                if (src.includes('?')) {
                  img.setAttribute('src', `${src}&format=webp`);
                } else {
                  img.setAttribute('src', `${src}?format=webp`);
                }
              }
            }
          }
        }
      }
    });
  };
  
  // Set up a MutationObserver to handle dynamically added images
  const observer = new MutationObserver((mutations) => {
    let hasNewImages = false;
    
    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            if (node.tagName === 'IMG' || node.querySelectorAll('img').length > 0) {
              hasNewImages = true;
            }
          }
        });
      }
    });
    
    if (hasNewImages) {
      processDynamicImages();
    }
  });
  
  // Start observing the document with configured parameters
  observer.observe(document.body, { 
    childList: true, 
    subtree: true 
  });
  
  // Tag all initial images to avoid reprocessing
  document.querySelectorAll('img').forEach(img => {
    img.setAttribute('processed-by-optimizer', 'true');
  });
}); 